import { ProfileStatus } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsLtaDecision'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'

const CTP_TYPE = RolodexEntryType.FFS_CRITICAL_THICKNESS_PROFILE

/**
 * Tri-state check for whether a usable critical-thickness profile exists for one
 * LTA. Extracted from `useGetCriticalProfiles` (which requires `IndicationContext`
 * and only distinguishes loading vs data) so it can run outside a per-LTA
 * provider (e.g. the pre-planning step) and expose the `unavailable` case that
 * gates Part-5 routing. "Usable" = at least one `FFS_CRITICAL_THICKNESS_PROFILE`
 * entry tagged to this LTA carrying a non-empty `critical_path_bins`.
 *
 * `indicationId` is required: every caller renders inside a selected LTA, and
 * accepting `undefined` would have to report "nothing to check" as `loading`,
 * which `ProfileStatus` has no honest state for.
 */
export function useLtaProfileStatus(indicationId: string): ProfileStatus {
  const { entries, isLoading } = useRolodexEntries(
    useMemo(
      () => [{ type: CTP_TYPE, tags: { indication: indicationId } }],
      [indicationId]
    )
  )

  return useMemo<ProfileStatus>(() => {
    if (isLoading) return 'loading'
    const usable = (entries as ReadonlyArray<GeckoEntry>).some((e) => {
      const bins = (e.data as { critical_path_bins?: unknown } | undefined)
        ?.critical_path_bins
      return Array.isArray(bins) && bins.length > 0
    })
    return usable ? 'usable' : 'unavailable'
  }, [entries, isLoading])
}
